import { useEffect, useMemo, useState } from 'react';
import { Check, Loader2, MoveRight, ScanSearch, Square, X } from 'lucide-react';

import type { TtqModelInvocationOptions } from '@kelilearning/psycheflow-contracts';

import { TtqAngleRelationIcon } from './TtqAngleRelationIcon';
import {
  TTQ_BOTTOM_PANEL_L1_WIDTH_PX,
  TTQ_BOTTOM_PANEL_SHELL_CLASS,
  TTQ_BOTTOM_PANEL_SHELL_STYLE,
} from './ttqBottomPanelShell';
import { TtqWorkbenchModelMenu } from './TtqWorkbenchModelMenu';
import {
  getTtqWorkbenchModelOption,
  readStoredTtqWorkbenchModelId,
  toTtqModelInvocation,
} from './ttqWorkbenchModels';

type TtqBottomPanel1qnaProps = {
  visible: boolean;
  sourceParticleId: string | null;
  submitting?: boolean;
  error?: string | null;
  errorDetail?: string | null;
  onClose: () => void;
  /** 触发 1qna/assess；评估结果确认后再生成 */
  onAssess: (payload: {
    sourceParticleId: string;
    description: string;
    model?: TtqModelInvocationOptions;
  }) => void;
};

const L1_DESCRIPTION = '同一道题探索多种解法路径，对比择优；生成新的解法知识颗粒。';

/** 一题多解底栏：先评估解法空间，再选解法提交生成（可含配图） */
export function TtqBottomPanel1qna({
  visible,
  sourceParticleId,
  submitting = false,
  error = null,
  errorDetail = null,
  onClose,
  onAssess,
}: TtqBottomPanel1qnaProps) {
  const [description, setDescription] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const [modelId, setModelId] = useState(() => readStoredTtqWorkbenchModelId());
  const selectedModel = useMemo(
    () => toTtqModelInvocation(getTtqWorkbenchModelOption(modelId)),
    [modelId],
  );

  useEffect(() => {
    if (!visible) {
      setDescription('');
      setDetailOpen(false);
      return;
    }
    setModelId(readStoredTtqWorkbenchModelId());
  }, [visible]);

  if (!visible || !sourceParticleId) return null;

  const canSubmit = !submitting;

  return (
    <div className={TTQ_BOTTOM_PANEL_SHELL_CLASS} style={TTQ_BOTTOM_PANEL_SHELL_STYLE}>
      <div className="flex items-center justify-between border-b border-border px-4 py-2">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-semibold text-ink">一题多解</span>
          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
            源题已选
          </span>
        </div>
        <button
          type="button"
          onClick={() => {
            setDescription('');
            onClose();
          }}
          className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-muted hover:bg-black/[0.05]"
        >
          <X size={14} />
          关闭
        </button>
      </div>

      <div className="overflow-x-auto overscroll-x-contain px-3 py-2 [-webkit-overflow-scrolling:touch]">
        <div className="flex w-full max-w-full flex-nowrap items-stretch justify-start gap-1.5">
          <div
            className="flex shrink-0 cursor-not-allowed flex-col items-start px-1 py-1 text-left text-muted"
            style={{ width: TTQ_BOTTOM_PANEL_L1_WIDTH_PX }}
            aria-disabled
            title="一题多解已锁定"
          >
            <div className="flex w-full items-start justify-between gap-2">
              <TtqAngleRelationIcon angleId="multi_solution" size="sm" muted />
              <span className="shrink-0 text-[10px] font-medium text-muted">一对多</span>
            </div>
            <p className="mt-1 line-clamp-2 text-xs text-muted">{L1_DESCRIPTION}</p>
            <span className="mt-1 text-[10px] text-muted">先评估 · 再选解生成</span>
          </div>
        </div>
      </div>

      <div className="border-t border-border px-4 py-3">
        <label className="mb-1.5 block text-[11px] font-medium text-muted">
          解法说明（可选）
        </label>
        <textarea
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          rows={2}
          placeholder="希望探索哪类解法方向，或补充约束…"
          className="w-full resize-none rounded-lg border border-border bg-canvas/60 px-3 py-2 text-sm text-ink outline-none focus:border-primary/40"
          disabled={submitting}
        />
        <p className="mt-1.5 text-[11px] text-muted">
          将先评估多解可行性，再选择路径生成；需图解的解法可生成嵌入配图。
        </p>
        {error ? (
          <div className="mt-1.5 rounded-lg border border-danger/30 bg-danger/5 px-3 py-2">
            <p className="text-xs leading-relaxed text-danger">{error}</p>
            {errorDetail ? (
              <button
                type="button"
                className="mt-1.5 inline-flex items-center gap-1 rounded border border-danger/35 px-2 py-0.5 text-[11px] font-medium text-danger hover:bg-danger/10"
                onClick={() => setDetailOpen(true)}
              >
                查看错误详情
              </button>
            ) : null}
          </div>
        ) : null}
        <div className="mt-2 flex items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-muted">
            <span className="font-medium text-ink/70">图例</span>
            <span className="inline-flex items-center gap-1"><Square size={12} />题</span>
            <span className="inline-flex items-center gap-1"><Check size={12} />解</span>
            <span className="inline-flex items-center gap-1"><MoveRight size={12} />变式</span>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <TtqWorkbenchModelMenu
              value={modelId}
              disabled={submitting}
              onChange={(option) => setModelId(option.id)}
            />
            <button
              type="button"
              disabled={!canSubmit}
              onClick={() => {
                onAssess({
                  sourceParticleId,
                  description: description.trim(),
                  model: selectedModel,
                });
              }}
              className={
                'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium '
                + (submitting || canSubmit
                  ? 'bg-primary text-white hover:bg-primary/90 disabled:opacity-90'
                  : 'cursor-not-allowed bg-black/[0.04] text-muted')
              }
            >
              {submitting
                ? <Loader2 size={14} className="animate-spin text-white" />
                : <ScanSearch size={14} />}
              {submitting ? '评估中…' : '评估解法空间'}
            </button>
          </div>
        </div>
      </div>

      {detailOpen && errorDetail ? (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/45 p-4">
          <div className="flex max-h-[80vh] w-full max-w-lg flex-col overflow-hidden rounded-xl border border-border bg-surface shadow-xl">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <p className="text-sm font-semibold text-ink">错误详情</p>
              <button type="button" className="text-muted hover:text-ink" onClick={() => setDetailOpen(false)}>
                <X size={16} />
              </button>
            </div>
            <pre className="min-h-0 flex-1 overflow-auto whitespace-pre-wrap px-4 py-3 text-[11px] leading-relaxed text-ink">
              {errorDetail}
            </pre>
            <div className="flex justify-end gap-2 border-t border-border px-4 py-3">
              <button
                type="button"
                className="rounded-md border border-border px-3 py-1.5 text-xs text-muted hover:bg-black/[0.03]"
                onClick={() => setDetailOpen(false)}
              >
                关闭
              </button>
              <button
                type="button"
                className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-white"
                onClick={() => {
                  void navigator.clipboard?.writeText(errorDetail);
                }}
              >
                复制详情
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
